import { buildPreviewMessage } from './whatsappPreview';
import { applyDoctorHonorificDedupeToBodyValues } from './whatsappDoctorHonorific';
import type { WhatsAppTemplate, WhatsAppTemplateButton } from '../../models/MasterAdmin/whatsappCampaign';
import type { WhatsAppTemplateVariableMapping } from '../../models/MasterAdmin/whatsappHub';
import type { DeepLinkCampaign } from '../../models/MasterAdmin/deepLink';
import { previewDeepLinkFromCampaign } from './whatsappDeepLinkPreview';

const SAMPLE_CONTEXT_VALUES: Record<string, string> = {
  doctorName: 'Dr. Rajesh Kapoor',
  vendorName: 'Dr. Rajesh Kapoor',
  patientName: 'Rahul Verma',
  userName: 'Rahul Verma',
  customerName: 'Rahul Verma',
  speciality: 'Dermatology',
  specialty: 'Dermatology',
  date: '25 Jul 2026',
  time: '10:30 AM',
  appointmentDate: '25 Jul 2026',
  appointmentTime: '10:30 AM',
  consultationType: 'Instant Video Consultation',
  clinicName: 'Vedika Clinic, Koramangala',
  orderId: 'ORD-48291',
  amount: '₹499',
  acceptDeepLink: 'https://vedikahealth.app/vendor/instant-consult/accept?broadcastId=bc_98123',
  deeplinkUrl: 'https://vedika.health/l/example',
  trackingUrl: 'https://vedika.health/track/amb-291',
  otp: '482910',
  medicineName: 'Paracetamol 500mg',
  labTestName: 'Complete Blood Count',
};

export function templateSampleBodyValues(template: WhatsAppTemplate): string[] {
  const count = Math.max(template.variableCount || 0, 0);
  return Array.from({ length: count }, (_, i) => `Sample ${i + 1}`);
}

export function templatePreviewMessage(template: WhatsAppTemplate): string {
  return buildPreviewMessage(template.bodyPreview || '', templateSampleBodyValues(template));
}

export function templateHeaderMimeType(format?: string | null): string | null {
  const f = String(format || '').toUpperCase();
  if (f === 'IMAGE') return 'image/jpeg';
  if (f === 'VIDEO') return 'video/mp4';
  if (f === 'DOCUMENT') return 'application/pdf';
  return null;
}

export function templateButtonTypeLabel(type: string): string {
  const t = type.toUpperCase();
  if (t === 'URL' || t === 'CALL_TO_ACTION') return 'URL button';
  if (t === 'QUICK_REPLY') return 'Quick reply';
  if (t === 'PHONE_NUMBER') return 'Call button';
  if (t === 'COPY_CODE') return 'Copy code';
  return type;
}

export function isCtaTemplateButton(btn: WhatsAppTemplateButton): boolean {
  const t = btn.type.toUpperCase();
  return t === 'URL' || t === 'PHONE_NUMBER' || t === 'COPY_CODE' || t === 'CALL_TO_ACTION';
}

export function resolveTemplateButtonPreviewUrl(
  btn: WhatsAppTemplateButton,
  campaignsById?: Map<string, DeepLinkCampaign>
): string | null {
  const type = String(btn.type || '').toUpperCase();
  if (type === 'PHONE_NUMBER') return String(btn.phoneNumber || '').trim() || null;
  if (type === 'COPY_CODE') return String(btn.code || '').trim() || null;
  if (type === 'URL' || type === 'CALL_TO_ACTION' || type === 'WEBSITE') {
    const campaignId = String(btn.deepLinkCampaignId || '').trim();
    if (campaignId && campaignsById?.size) {
      const fromDeepLink = previewDeepLinkFromCampaign(
        {
          deepLinkCampaignId: campaignId,
          deepLinkUrlType: btn.deepLinkUrlType || 'short',
          deepLinkParamBindings: btn.deepLinkParamBindings || {},
        },
        campaignsById
      );
      if (fromDeepLink) return fromDeepLink;
    }
    return String(btn.url || '').trim() || null;
  }
  return null;
}

export function buttonsWithResolvedPreviewUrls(
  buttons: WhatsAppTemplateButton[] | null | undefined,
  campaignsById?: Map<string, DeepLinkCampaign>
): WhatsAppTemplateButton[] {
  return (buttons || []).map((btn) => {
    const resolved = resolveTemplateButtonPreviewUrl(btn, campaignsById);
    if (!resolved) return btn;
    const type = String(btn.type || '').toUpperCase();
    if (type === 'PHONE_NUMBER') return { ...btn, phoneNumber: resolved };
    if (type === 'COPY_CODE') return { ...btn, code: resolved };
    return { ...btn, url: resolved };
  });
}

export function sampleValueForMapping(
  mapping: WhatsAppTemplateVariableMapping,
  campaignsById?: Map<string, DeepLinkCampaign>
): string {
  if (mapping.sourceType === 'deep_link' && mapping.deepLinkCampaignId && campaignsById) {
    const url = previewDeepLinkFromCampaign(
      {
        deepLinkCampaignId: mapping.deepLinkCampaignId,
        deepLinkUrlType: mapping.deepLinkUrlType,
        deepLinkParamBindings: mapping.deepLinkParamBindings,
      },
      campaignsById
    );
    if (url) return url;
  }
  if (mapping.sourceType === 'static' && mapping.staticValue?.trim()) {
    return mapping.staticValue.trim();
  }
  const key = mapping.contextKey?.trim();
  if (key && SAMPLE_CONTEXT_VALUES[key]) return SAMPLE_CONTEXT_VALUES[key];
  return mapping.label?.trim() || `Sample ${mapping.slot}`;
}

export function bodyValuesFromMappings(
  variableCount: number,
  mappings?: WhatsAppTemplateVariableMapping[],
  campaignsById?: Map<string, DeepLinkCampaign>,
  bodyPreview?: string | null
): string[] {
  const count = Math.max(variableCount || 0, 0);
  const values = Array.from({ length: count }, (_, i) => {
    const slot = i + 1;
    const mapping = mappings?.find((m) => m.slot === slot);
    if (mapping) return sampleValueForMapping(mapping, campaignsById);
    return `Sample ${slot}`;
  });

  const orderedMappings = Array.from({ length: count }, (_, i) => {
    const slot = i + 1;
    return mappings?.find((m) => m.slot === slot) || { slot, contextKey: null };
  });

  return applyDoctorHonorificDedupeToBodyValues({
    bodyPreview,
    mappings: orderedMappings,
    values,
  });
}

export function notificationPreviewFromTemplate(
  template: WhatsAppTemplate,
  mappings?: WhatsAppTemplateVariableMapping[],
  campaignsById?: Map<string, DeepLinkCampaign>
) {
  const bodyValues = bodyValuesFromMappings(
    template.variableCount,
    mappings,
    campaignsById,
    template.bodyPreview
  );
  return {
    ...devicePreviewFromTemplate(template, { campaignsById }),
    message: buildPreviewMessage(template.bodyPreview || '', bodyValues),
  };
}

export function devicePreviewFromTemplate(
  template: WhatsAppTemplate,
  opts?: {
    headerMediaUrl?: string | null;
    headerMimeType?: string | null;
    campaignsById?: Map<string, DeepLinkCampaign>;
  }
) {
  return {
    message: templatePreviewMessage(template),
    headerText:
      String(template.headerFormat || '').toUpperCase() === 'TEXT' ? template.headerText || null : null,
    footer: template.footer || null,
    buttons: buttonsWithResolvedPreviewUrls(template.buttons, opts?.campaignsById),
    headerMediaUrl: opts?.headerMediaUrl ?? null,
    headerMimeType: opts?.headerMimeType ?? templateHeaderMimeType(template.headerFormat),
  };
}
